import React, { useEffect } from 'react';
import { MultipleSelect, SingleSelect } from 'react-select-material-ui';
import { generateLabel } from '../../utils/helpers';
import useStyles from './styles';
import { usePermissions } from '../../hooks/usePermissions';

type CommunityOption = {
  value: number;
  label: string;
  city?: string;
  state?: string;
  zipcode?: string;
  lasso_community_project_id?: string;
  novihome_integration?: string;
  community_backup_code?: string;
  trade_code?: string;
  sales_code?: string;
  url?: string;
  sales_agent?: string;
};

type Props = {
  getBuilderCommunities: (builderId: number) => void;
  creatable?: boolean;
  communityExternalIds: string[];
  builderCommunities: {
    id: number;
    name: string;
    city?: string;
    state?: string;
    zip?: string;
  }[];
  builderId: number;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  label?: string;

  multiple?: boolean;
  handleChange: (value: any, rest?: any) => void;
  initialValue?: number | number[] | null;
};

const CommunitySearch: React.FC<Props> = ({
  handleChange,
  initialValue,
  multiple = false,
  getBuilderCommunities,
  builderId,
  builderCommunities,
  creatable = true,
  required = false,
  error = false,
  helperText = '',
  label,
}) => {
  const classes = useStyles();

  const { canEdit } = usePermissions();

  useEffect(() => {
    getBuilderCommunities(builderId);
  }, [getBuilderCommunities, builderId]);

  const options: CommunityOption[] = builderCommunities.map((community: {
    id: number;
    name: string;
    city?: string;
    state?: string;
    zip?: string;
    lasso_community_project_id?: string;
    novihome_integration?: string;
    community_backup_code?: string;
    trade_code?: string;
    sales_code?: string;
    community_url?: string;
    sales_agent?: string;
  }) => ({
    value: community.id,
    label: community.name,
    city: community.city || '',
    state: community.state || '',
    zipcode: community.zip || '',
    lasso_community_project_id: community.lasso_community_project_id,
    novihome_integration: community.novihome_integration,
    community_backup_code: community.community_backup_code,
    trade_code: community.trade_code,
    sales_code: community.sales_code,
    url: community.community_url,
    sales_agent: community.sales_agent,
  }));

  if (multiple) {
    return (
      <MultipleSelect
        className={`${classes.root} ${error ? classes.error : ''}`}
        label={generateLabel(label || `Community Names${required ? '*' : ''}`)}
        options={options}
        onChange={(value: any) => handleChange(value || [])}
        value={Array.isArray(initialValue) ? initialValue : []}
        SelectProps={{
          isCreatable: creatable,
          msgNoOptionsMatchFilter: 'No community names was found',
        }}
        required={required}
        error={error}
        helperText={helperText}
        disabled={!canEdit}
      />
    );
  }

  return (
    <SingleSelect
      className={`${classes.root} ${error ? classes.error : ''}`}
      label={generateLabel(label || `Community Name${required ? '*' : ''}`)}
      options={options}
      onChange={handleChange}
      value={typeof initialValue === 'number' ? initialValue : null}
      SelectProps={{
        isCreatable: creatable,
        msgNoOptionsMatchFilter: 'No community names was found',
      }}
      required={required}
      error={error}
      helperText={helperText}
      disabled={!canEdit}
    />
  );
};

export default CommunitySearch;
